import { toast } from '@tempo-io/sui-sonner';
import debounce from 'lodash.debounce';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildFilterString, ItemsApi } from '@/common/api/items';
import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import { LoopPlannerScenarioItemsApi } from '@/features/planner/api/scenarioItems';

export interface PickerOption {
  id: number;
  name: string;
  itemTypeId: number;
  externalKey?: string | null;
}

export const SEARCH_RESULTS_LIMIT = 50;
export const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_DELAY_MS = 300;

const EMPTY_ARRAY: number[] = [];

interface UseItemPickerOptionsParams {
  filter?: ItemFiltersType;
  excludedItemIds?: number[];
  eagerLoad?: boolean;
  withExternalKey?: boolean;
  loopId?: number;
  scenarioId?: number;
}

const buildItemFilters = (
  inputValue: string,
  withExternalKey: boolean,
  additionalFilters?: ItemFiltersType,
): ItemFiltersType => {
  const filters: ItemFiltersType = { ...additionalFilters };
  const trimmed = inputValue.trim();

  if (trimmed.length < MIN_SEARCH_LENGTH) {
    return filters;
  }

  if (withExternalKey) {
    filters.name = { value: trimmed, operator: 'containsIgnoreCase' };
    filters.externalKey = { value: trimmed, operator: 'containsIgnoreCase' };
    return filters;
  }

  filters.name = { value: trimmed, operator: 'startsWithIgnoreCase' };
  return filters;
};

export const useItemPickerOptions = ({
  filter,
  excludedItemIds = EMPTY_ARRAY,
  eagerLoad = false,
  withExternalKey = false,
  loopId,
  scenarioId,
}: UseItemPickerOptionsParams) => {
  const { t } = useTranslation();

  const fetchItems = useMemo(
    () =>
      debounce((inputValue: string, callback: (options: PickerOption[]) => void) => {
        if (!eagerLoad && inputValue.trim().length < MIN_SEARCH_LENGTH) {
          callback([]);
          return;
        }

        const fetchAsync = async () => {
          try {
            const filters = buildItemFilters(inputValue, withExternalKey, filter);
            const limit = eagerLoad
              ? SEARCH_RESULTS_LIMIT + excludedItemIds.length
              : SEARCH_RESULTS_LIMIT;

            const options =
              loopId !== undefined && scenarioId !== undefined
                ? (
                    await LoopPlannerScenarioItemsApi.getScenarioItems({
                      loopId,
                      scenarioId,
                      filter: buildFilterString(filters),
                      limit,
                    })
                  ).data
                    .filter((scenarioItem) => !excludedItemIds.includes(scenarioItem.itemId))
                    .map((scenarioItem) => ({
                      id: scenarioItem.itemId,
                      name: scenarioItem.name,
                      itemTypeId: scenarioItem.itemTypeId,
                      externalKey: scenarioItem.externalKey,
                    }))
                : (
                    await ItemsApi.getItems({
                      filter: filters,
                      order: 'name',
                      limit,
                      hierarchical: false,
                    })
                  ).data
                    .filter((item) => !excludedItemIds.includes(item.id))
                    .map((item) => ({
                      id: item.id,
                      name: item.name,
                      itemTypeId: item.itemTypeId,
                      externalKey: item.externalKey,
                    }));

            callback(options);
          } catch (_error) {
            toast.error(t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.loadError));
            callback([]);
          }
        };
        fetchAsync();
      }, DEBOUNCE_DELAY_MS),
    [filter, t, excludedItemIds, eagerLoad, withExternalKey, loopId, scenarioId],
  );

  useEffect(() => () => fetchItems.cancel(), [fetchItems]);

  return fetchItems;
};
